import type { FormNode } from '@api/importApi';

/**
 * Path helpers + immutable mutators для FormNode-tree (LayoutEditor).
 * Виокремлено з LayoutTreeEditor.tsx щоб мати unit-тести без рендеру React.
 */

export interface NodePath {
  /** Шлях індексів у nodes (top-level: [3], всередині group: [3, 1]). */
  indices: number[];
}

export function pathKey(p: NodePath): string {
  return p.indices.join('.');
}

export function getNodeAtPath(acqNodes: FormNode[], acqPath: NodePath): FormNode | null {
  let acqList: FormNode[] | undefined = acqNodes;
  let acqNode: FormNode | undefined;
  for (const i of acqPath.indices) {
    if (!acqList) return null;
    acqNode = acqList[i];
    acqList = acqNode?.children;
  }
  return acqNode ?? null;
}

export function updateNodeAtPath(acqNodes: FormNode[], acqPath: NodePath, acqMutator: (n: FormNode) => FormNode): FormNode[] {
  if (acqPath.indices.length === 0) return acqNodes;
  const [acqHead, ...acqRest] = acqPath.indices;
  return acqNodes.map((acqN, i) => {
    if (i !== acqHead) return acqN;
    if (acqRest.length === 0) return acqMutator(acqN);
    return { ...acqN, children: updateNodeAtPath(acqN.children ?? [], { indices: acqRest }, acqMutator) };
  });
}

export function deleteNodeAtPath(acqNodes: FormNode[], acqPath: NodePath): FormNode[] {
  if (acqPath.indices.length === 0) return acqNodes;
  const [acqHead, ...acqRest] = acqPath.indices;
  if (acqRest.length === 0) return acqNodes.filter((_, i) => i !== acqHead);
  return acqNodes.map((acqN, i) => {
    if (i !== acqHead) return acqN;
    return { ...acqN, children: deleteNodeAtPath(acqN.children ?? [], { indices: acqRest }) };
  });
}

export function reorderAtParent(acqNodes: FormNode[], acqParentPath: number[], acqOldIdx: number, acqNewIdx: number): FormNode[] {
  if (acqParentPath.length === 0) return arrayMoveLocal(acqNodes, acqOldIdx, acqNewIdx);
  const [acqHead, ...acqRest] = acqParentPath;
  return acqNodes.map((acqN, i) => {
    if (i !== acqHead) return acqN;
    return { ...acqN, children: reorderAtParent(acqN.children ?? [], acqRest, acqOldIdx, acqNewIdx) };
  });
}

export function addChildAtPath(acqNodes: FormNode[], acqParentPath: number[], acqNewNode: FormNode): FormNode[] {
  if (acqParentPath.length === 0) return [...acqNodes, acqNewNode];
  const [acqHead, ...acqRest] = acqParentPath;
  return acqNodes.map((acqN, i) => {
    if (i !== acqHead) return acqN;
    return { ...acqN, children: addChildAtPath(acqN.children ?? [], acqRest, acqNewNode) };
  });
}

export function insertChildAtIndex(acqNodes: FormNode[], acqParentPath: number[], acqIndex: number, acqNewNode: FormNode): FormNode[] {
  if (acqParentPath.length === 0) {
    const acqCopy = [...acqNodes];
    acqCopy.splice(Math.max(0, Math.min(acqIndex, acqCopy.length)), 0, acqNewNode);
    return acqCopy;
  }
  const [acqHead, ...acqRest] = acqParentPath;
  return acqNodes.map((acqN, i) => {
    if (i !== acqHead) return acqN;
    return { ...acqN, children: insertChildAtIndex(acqN.children ?? [], acqRest, acqIndex, acqNewNode) };
  });
}

/** Cross-container перенесення вузла. Повертає новий nodes-tree або null якщо move недопустимий. */
export function moveNode(
  acqNodes: FormNode[],
  acqFromPath: number[],
  acqToParentPath: number[],
  acqToIndex: number,
): FormNode[] | null {
  if (acqFromPath.length === 0) return null;
  // Заборонити переміщувати вузол у самого себе або у власного нащадка
  if (acqToParentPath.length >= acqFromPath.length
      && acqFromPath.every((v, i) => acqToParentPath[i] === v)) {
    return null;
  }
  const acqMoving = getNodeAtPath(acqNodes, { indices: acqFromPath });
  if (!acqMoving) return null;

  const acqAfterDelete = deleteNodeAtPath(acqNodes, { indices: acqFromPath });

  // Скоригувати destination path: якщо destination мав той самий prefix що й source,
  // і index у спільному рівні був більший за source — зменшити на 1.
  let acqAdjustedParent = [...acqToParentPath];
  let acqAdjustedIdx = acqToIndex;
  const acqSrcParent = acqFromPath.slice(0, -1);
  const acqSrcLast = acqFromPath[acqFromPath.length - 1];
  if (acqAdjustedParent.length === acqSrcParent.length
      && acqAdjustedParent.every((v, i) => v === acqSrcParent[i])
      && acqAdjustedIdx > acqSrcLast) {
    acqAdjustedIdx -= 1;
  }
  else if (acqAdjustedParent.length > acqSrcParent.length
           && acqSrcParent.every((v, i) => acqAdjustedParent[i] === v)
           && acqAdjustedParent[acqSrcParent.length] > acqSrcLast) {
    acqAdjustedParent[acqSrcParent.length] -= 1;
  }

  return insertChildAtIndex(acqAfterDelete, acqAdjustedParent, acqAdjustedIdx, acqMoving);
}

// ─────────────────────── DND ID HELPERS ───────────────────────

export function pathToDndId(acqPath: number[]): string {
  return `node:${acqPath.join('-')}`;
}

export function dndIdToPath(acqId: string): number[] | null {
  if (!acqId.startsWith('node:')) return null;
  const acqRest = acqId.substring('node:'.length);
  if (acqRest === '') return [];
  return acqRest.split('-').map((s) => parseInt(s, 10));
}

export function isContainerType(acqType: FormNode['type']): boolean {
  return acqType === 'group' || acqType === 'page' || acqType === 'pages';
}

// Локальний arrayMove (без залежності від @dnd-kit/sortable у helpers-файлі — щоб тести не тягнули React DnD)
function arrayMoveLocal<T>(acqArr: T[], acqFrom: number, acqTo: number): T[] {
  const acqCopy = [...acqArr];
  const [acqItem] = acqCopy.splice(acqFrom, 1);
  acqCopy.splice(acqTo, 0, acqItem);
  return acqCopy;
}
